import {
  Configuration,
  MessagesApi,
  ChatsApi,
  SharedChatMessagesApi,
} from '@/infrastructure/api/client_typescript';
import type {
  DocumentPreview as DocumentDto,
  ResponseMessage as MessageDto,
  RequestMessageFeedback,
} from '@/infrastructure/api/client_typescript';
import type {
  Chat,
  ChatMessage,
  ChatMessageRelatedDocument,
  UserSettings,
} from '@/types/chat';
import { TokenStore } from '@/infrastructure/storage/TokenStore';
import { ServiceLocator } from '@/services/ServiceLocator';

function convertDocumentFromDto(dto: DocumentDto): ChatMessageRelatedDocument {
  return {
    id: dto.id,
    title: dto.title,
  };
}

function convertMessageFromDto(dto: MessageDto): ChatMessage {
  const text = dto.text || '';
  /* eslint-disable @typescript-eslint/no-explicit-any */
  // TODO: Remove this temporary placeholder once backend returns real thinking content
  const thinking =
    (dto as any).thinking || (dto.type === 'bot' && dto.status === 'pending' ? 'Analyzing your question and searching for relevant information...' : '');
  const metadata = (dto as any).metadata ?? null;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  const contextDocs = dto.context_documents ?? [];
  const contextFolders = dto.context_folders ?? [];

  return {
    id: dto.id,
    sender: dto.type,
    thinking: thinking,
    text: text,
    status: dto.status,
    relatedDocuments: dto.related_documents
      ? dto.related_documents.map(convertDocumentFromDto)
      : null,
    related_client_history_passages: dto.related_client_history_passages ?? [],
    related_case_law_passages: dto.related_case_law_passages ?? [],
    metadata: metadata,
    context_documents: contextDocs,
    context_document_ids: contextDocs.map((doc) => doc.id),
    selectedDocuments: contextDocs.map((doc) => ({
      id: doc.id,
      title: doc.title,
      folder: doc.folder ?? '',
    })),
    context_folders: contextFolders,
    context_folder_ids: contextFolders.map((folder) => folder.id),
    selectedFolders: contextFolders.map((folder) => ({
      id: folder.id,
      title: folder.title, // title is correct field name
    })),
    model: '',
  };
}

export class ChatService {
  private messagesApi: MessagesApi;
  private chatApi: ChatsApi;
  private sharedChatMessagesApi: SharedChatMessagesApi;

  constructor(
    apiBaseUrl: string,
    private tokenStore: TokenStore
  ) {
    // Important: accessToken as a FUNCTION → always reads the latest token.
    const config = new Configuration({
      basePath: apiBaseUrl,
      accessToken: () => this.tokenStore.getAccess() || '',
    });

    this.messagesApi = new MessagesApi(config);
    this.chatApi = new ChatsApi(config);
    this.sharedChatMessagesApi = new SharedChatMessagesApi(config);
  }

  async sendMessageInExistingChat(
    chat_id: string,
    message: string,
    context_document_ids: string[],
    context_folder_ids: string[],
    followLinks: boolean,
    model: string
  ) {
    const request = {
      text: message,
      context_document_ids: context_document_ids,
      context_folder_ids: context_folder_ids,
      follow_links: followLinks,
      model: model,
    };
    return ServiceLocator.getSessionManager().makeAuthenticatedRequest(
      async () => {
        const response = await this.messagesApi.createMessage(chat_id, request);
        return response.data;
      }
    );
  }

  async sendMessageInNewChat(
    message: string,
    context_document_ids: string[],
    context_folder_ids: string[],
    followLinks: boolean,
    model: string
  ) {
    const request = {
      text: message,
      context_document_ids: context_document_ids,
      context_folder_ids: context_folder_ids,
      follow_links: followLinks,
      model: model,
    };
    return ServiceLocator.getSessionManager().makeAuthenticatedRequest(
      async () => {
        const response = await this.messagesApi.createChat(request);
        return response.data;
      }
    );
  }

  async getChatMessages(chat_id: string) {
    return ServiceLocator.getSessionManager().makeAuthenticatedRequest(
      async () => {
        const response = await this.messagesApi.listMessages(chat_id);
        return response.data;
      }
    );
  }

  async deleteChat(chat_id: string) {
    return ServiceLocator.getSessionManager().makeAuthenticatedRequest(
      async () => {
        const response = await this.chatApi.deleteChat(chat_id);
        return response.data;
      }
    );
  }

  async listChats() {
    return ServiceLocator.getSessionManager().makeAuthenticatedRequest(
      async () => {
        const response = await this.chatApi.listChats();

        const chats: Chat[] = [];
        for (const chat of response.data.chats) {
          chats.push({
            id: chat.id,
            messages: [],
            last_message_at: new Date(chat.last_message_at * 1000),
            title: chat.title,
          });
        }
        return chats;
      }
    );
  }

  async getChat(chat_id: string) {
    return ServiceLocator.getSessionManager().makeAuthenticatedRequest(
      async () => {
        const response = await this.chatApi.getChat(chat_id);
        return response.data;
      }
    );
  }

  async listChatMessages(chat_id: string) {
    return ServiceLocator.getSessionManager().makeAuthenticatedRequest(
      async () => {
        const response = await this.messagesApi.listMessages(chat_id);
        let messages: ChatMessage[] = [];
        let userSettings: UserSettings | undefined = undefined;

        if (Array.isArray(response.data)) {
          messages = response.data.map(convertMessageFromDto);
        } else if (response.data && response.data.messages) {
          messages = response.data.messages.map(convertMessageFromDto);
          userSettings = response.data.user_settings;
        }
        return { messages, userSettings };
      }
    );
  }

  async getChatMessage(chat_id: string, message_id: string) {
    return ServiceLocator.getSessionManager().makeAuthenticatedRequest(
      async () => {
        const response = await this.messagesApi.getMessage(chat_id, message_id);
        return convertMessageFromDto(response.data);
      }
    );
  }

  async getMessagesFromSharedLink(
    sharedLinkId: string
  ): Promise<ChatMessage[]> {
    try {
      const response =
        await this.sharedChatMessagesApi.listSharedChatMessages(sharedLinkId);
      return response.data.messages.map(convertMessageFromDto);
    } catch (error) {
      throw error;
    }
  }

  async addMessageFeedback(feedback: RequestMessageFeedback) {
    return ServiceLocator.getSessionManager().makeAuthenticatedRequest(
      async () => {
        return await this.messagesApi.addMessageFeedback(feedback);
      }
    );
  }

  async stopMessageGeneration(
    chat_id: string,
    message_id: string
  ): Promise<void> {
    return ServiceLocator.getSessionManager().makeAuthenticatedRequest(
      async () => {
        await this.messagesApi.stopMessageGeneration(chat_id, message_id);
      }
    );
  }
}